import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_URL, Pipeline } from '../../types';
import CodeViewer from '../../components/CodeViewer';
import { useTheme } from '../../ThemeContext';
import {apiGet, apiPost} from "../../utils/apiClient";

export default function PipelineDetailsPage() {
  const { theme } = useTheme();
  const [duplicating, setDuplicating] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [versions, setVersions] = useState<Array<{ version: string }>>([]);
  const [selectedVersion, setSelectedVersion] = useState<string | ''>('');
  const [switchingVersion, setSwitchingVersion] = useState(false);

  const currentVersionLabel = useMemo(() => {
    const v = selectedVersion || pipeline?.version || '';
    return v ? `v${v}` : 'current';
  }, [selectedVersion, pipeline?.version]);

  const loadPipeline = async (pid: string) => {
    const res = await apiGet(`/api/pipelines/${pid}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data?.detail || 'Failed to load pipeline');
    setPipeline(data);
  };

  const loadVersions = async (pid: string) => {
    try {
      const res = await apiGet(`/api/pipelines/${pid}/versions`);
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        // Only map to versions and make unique, most recent first
        const vs = Array.from(new Set(data.map((v: any) => v.version))).filter(Boolean);
        setVersions(vs.map(v => ({ version: v })));
      }
    } catch (e) {
      // ignore versions error; dropdown will be hidden if none
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!id) return;
      try {
        await loadPipeline(id as string);
        await loadVersions(id as string);
        // If a version is specified in the query params, auto-switch to it
        const params = new URLSearchParams(location.search || '');
        const v = params.get('version');
        if (v) {
          await handleVersionChange(v);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load pipeline');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id, location.search]);

  const handleVersionChange = async (version: string) => {
    if (!id) return;
    setSelectedVersion(version);
    if (!version) {
      // Load current
      setSwitchingVersion(true);
      try {
        await loadPipeline(id as string);
      } catch (e: any) {
        setError(e?.message || 'Failed to load pipeline');
      } finally {
        setSwitchingVersion(false);
      }
      return;
    }
    setSwitchingVersion(true);
    try {
      const res = await apiGet(`/api/pipelines/${id}/versions/${version}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to load version');
      setPipeline(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load version');
    } finally {
      setSwitchingVersion(false);
    }
  };

  const handleDuplicate = async () => {
    if (!pipeline) return;
    const defaultName = `${pipeline.name} (copy)`;
    const newName = window.prompt('Enter a name for the duplicated pipeline', defaultName || 'New Pipeline');
    if (!newName) return; // cancelled
    try {
      setDuplicating(true);
      const tgt: any = (pipeline as any).target || {};
      const payload: any = {
        name: newName,
        description: pipeline.description || '',
        source: (pipeline as any).source,
        transformations: (pipeline as any).transformations || [],
        target: { ...tgt, mode: tgt.mode || 'overwrite' },
        schedule: (pipeline as any).schedule ?? null,
      };
      const res = await apiPost(`/api/pipelines`, payload);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to duplicate pipeline');
      // Navigate to new pipeline details
      navigate(`/pipelines/${data.pipeline_id}`);
    } catch (e: any) {
      alert(e?.message || 'Failed to duplicate pipeline');
    } finally {
      setDuplicating(false);
    }
  };

  const handleCopy = async () => {
    if (!pipeline) return;
    try {
      const tgt: any = (pipeline as any).target || {};
      const configToCopy = {
        name: pipeline.name,
        description: pipeline.description || '',
        source: (pipeline as any).source,
        transformations: (pipeline as any).transformations || [],
        target: { ...tgt, mode: tgt.mode || 'overwrite' },
        schedule: (pipeline as any).schedule ?? null,
      };
      await navigator.clipboard.writeText(JSON.stringify(configToCopy, null, 2));
      alert('Pipeline configuration copied to clipboard!');
    } catch (e: any) {
      alert(e?.message || 'Failed to copy pipeline configuration');
    }
  };

  if (loading) return <div style={{ color: theme.text }}>Loading…</div>;
  if (error) return <div style={{ color: theme.error }}>{error}</div>;
  if (!pipeline) return <div style={{ color: theme.text }}>Not found</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: theme.text }}>{pipeline.name}</h2>
          <p style={{ margin: '6px 0 0 0', color: theme.textSecondary }}>{pipeline.description}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Version dropdown */}
          <div>
            <label htmlFor="ver-dd" style={{ fontSize: 12, color: theme.textSecondary, display: 'block' }}>Version</label>
            <select
              id="ver-dd"
              value={selectedVersion}
              onChange={e => handleVersionChange(e.target.value)}
              style={{ padding: '6px 8px', border: `1px solid ${theme.inputBorder}`, background: theme.inputBackground, color: theme.text, borderRadius: 6, minWidth: 140 }}
              title="Select a historical version to view"
            >
              <option value="">Current {pipeline.version ? `(v${pipeline.version})` : ''}</option>
              {versions.map(v => (
                <option key={v.version} value={v.version}>v{v.version}</option>
              ))}
            </select>
          </div>
          <button onClick={() => navigate(`/pipelines/${pipeline.pipeline_id}/edit`)} style={{ padding: '8px 12px', border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, borderRadius: 6, cursor: 'pointer' }}>Edit</button>
          <button onClick={handleCopy} title="Copy pipeline configuration to clipboard" style={{ padding: '8px 12px', border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, borderRadius: 6, cursor: 'pointer' }}>Copy</button>
          <button onClick={handleDuplicate} disabled={duplicating} title="Duplicate this pipeline" style={{ padding: '8px 12px', border: `1px solid ${theme.border}`, background: duplicating ? theme.hoverBackground : theme.cardBackground, color: theme.text, borderRadius: 6, cursor: duplicating ? 'not-allowed' : 'pointer' }}>{duplicating ? 'Duplicating…' : 'Duplicate'}</button>
          <button onClick={() => navigate(-1)} style={{ padding: '8px 12px', border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, borderRadius: 6, cursor: 'pointer' }}>Back</button>
        </div>
      </div>

      {switchingVersion && <div style={{ color: theme.textSecondary, marginBottom: 8 }}>Loading {currentVersionLabel}…</div>}

      <section style={{ marginBottom: 20 }}>
        <h3 style={{ color: theme.textSecondary, marginBottom: 10 }}>Source</h3>
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12, background: theme.cardBackground }}>
          {(() => {
            const src: any = (pipeline as any).source || {};
            const type = src?.type || 'jdbc';
            const conn = src?.connection;
            const opts = src?.options || {};
            if (type === 'jdbc' && conn) {
              return (
                <>
                  <p style={{ margin: '0 0 6px 0', fontSize: 14, color: theme.text }}><strong>Table:</strong> {conn.table}</p>
                  <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary }}>{conn.url}</p>
                </>
              );
            }
            if (type === 'excel_directory') {
              return (
                <div style={{ fontSize: 13, color: theme.text }}>
                  <p style={{ margin: '0 0 6px 0' }}><strong>Type:</strong> Excel directory</p>
                  <p style={{ margin: '0 0 4px 0' }}><strong>Path:</strong> {opts.path || '—'}</p>
                  <p style={{ margin: '0 0 4px 0' }}><strong>Pattern:</strong> {opts.pattern || '*.xlsx'}</p>
                  {opts.sheet && <p style={{ margin: '0 0 4px 0' }}><strong>Sheet:</strong> {opts.sheet}</p>}
                  {opts.dataAddress && <p style={{ margin: '0 0 4px 0' }}><strong>Data Address:</strong> {opts.dataAddress}</p>}
                  <p style={{ margin: '0 0 4px 0' }}><strong>Header:</strong> {String(opts.header ?? true)}</p>
                  <p style={{ margin: 0 }}><strong>Infer schema:</strong> {String(opts.inferSchema ?? true)}{opts.recursive !== undefined ? ` | Recursive: ${String(opts.recursive)}` : ''}</p>
                </div>
              );
            }
            if (type === 'csv_directory') {
              return (
                <div style={{ fontSize: 13, color: theme.text }}>
                  <p style={{ margin: '0 0 6px 0' }}><strong>Type:</strong> CSV directory</p>
                  <p style={{ margin: '0 0 4px 0' }}><strong>Path:</strong> {opts.path || '—'}</p>
                  <p style={{ margin: '0 0 4px 0' }}><strong>Pattern:</strong> {opts.pattern || '*.csv'}</p>
                  <p style={{ margin: 0 }}><strong>Header:</strong> {String(opts.header ?? true)} | <strong>Infer schema:</strong> {String(opts.inferSchema ?? true)}</p>
                </div>
              );
            }
            if (type === 'parquet_directory') {
              return (
                <div style={{ fontSize: 13, color: theme.text }}>
                  <p style={{ margin: '0 0 6px 0' }}><strong>Type:</strong> Parquet directory</p>
                  <p style={{ margin: '0 0 4px 0' }}><strong>Path:</strong> {opts.path || '—'}</p>
                  <p style={{ margin: 0 }}><strong>Pattern:</strong> {opts.pattern || '*.parquet'}</p>
                </div>
              );
            }
            return (
              <CodeViewer
                value={JSON.stringify(src, null, 2)}
                language="json"
                height="150px"
              />
            );
          })()}
        </div>
      </section>

      <section style={{ marginBottom: 20 }}>
        <h3 style={{ color: theme.textSecondary, marginBottom: 10 }}>Transformations ({pipeline.transformations.length})</h3>
        {pipeline.transformations.map((t, idx) => (
          <div key={t.id || idx} style={{ border: `1px solid ${theme.border}`, background: theme.cardBackground, padding: 10, borderRadius: 8, marginBottom: 8 }}>
            <p style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 600, color: theme.text }}>{idx + 1}. {t.type}</p>
            {/* Show full JSON for this transformation */}
            {t.type === 'custom_sql' ? (
              <div>
                <div style={{ marginBottom: 8 }}>
                    <CodeViewer
                        value={JSON.stringify({ ...t, config: { ...t.config, sql: undefined } }, null, 2)}
                        language="json"
                        height="100px"
                    />
                    <p style={{ margin: '4px 0', fontSize: 12, color: theme.textSecondary }}><strong>SQL:</strong></p>
                  <CodeViewer
                    value={typeof (t as any).config?.sql === 'string' ? (t as any).config.sql.replace(/\\n/g, '\n') : String((t as any).config?.sql || '')}
                    language="text"
                    height="500px"
                  />
                </div>

              </div>
            ) : t.type === 'pyspark' ? (
              <div>
                <div style={{ marginBottom: 8 }}>
                    <CodeViewer
                        value={JSON.stringify({ ...t, config: { ...t.config, code: undefined } }, null, 2)}
                        language="json"
                        height="100px"
                    />
                    <p style={{ margin: '4px 0', fontSize: 12, color: theme.textSecondary }}><strong>PySpark Code:</strong></p>
                  <CodeViewer
                    value={typeof (t as any).config?.code === 'string' ? (t as any).config.code.replace(/\\n/g, '\n') : String((t as any).config?.code || '')}
                    language="python"
                    height="500px"
                  />
                </div>

              </div>
            ) : (
              <CodeViewer
                value={JSON.stringify(t, null, 2)}
                language="json"
                height="160px"
              />
            )}
          </div>
        ))}
      </section>

      <section style={{ marginBottom: 20 }}>
        <h3 style={{ color: theme.textSecondary, marginBottom: 10 }}>Target</h3>
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12, background: theme.cardBackground }}>
          {(() => {
            const tgt: any = (pipeline as any).target || {};
            const type = tgt?.type || 'jdbc';
            const conn = tgt?.connection;
            if (type === 'jdbc' && conn) {
              return (
                <>
                  <p style={{ margin: '0 0 6px 0', fontSize: 14, color: theme.text }}><strong>Table:</strong> {conn.table}</p>
                  <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary }}>{conn.url}</p>
                </>
              );
            }
            return (
              <CodeViewer
                value={JSON.stringify(tgt, null, 2)}
                language="json"
                height="180px"
              />
            );
          })()}
        </div>
      </section>

      {/* Artifacts Section */}
      {(pipeline as any).artifacts && Array.isArray((pipeline as any).artifacts) && (pipeline as any).artifacts.length > 0 && (
        <section style={{ marginBottom: 20 }}>
          <h3 style={{ color: theme.textSecondary, marginBottom: 10 }}>Artifacts Configuration</h3>
          <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12, background: theme.cardBackground }}>
            <p style={{ margin: '0 0 12px 0', fontSize: 13, color: theme.textSecondary }}>
              Files or directories that will be available for download after job execution.
            </p>
            {((pipeline as any).artifacts as Array<{ name: string; path: string; description?: string }>).map((artifact, index) => (
              <div
                key={index}
                style={{
                  padding: 12,
                  marginBottom: index < ((pipeline as any).artifacts as Array<any>).length - 1 ? 8 : 0,
                  background: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                }}
              >
                <p style={{ margin: '0 0 6px 0', fontSize: 14, fontWeight: 600, color: theme.text }}>
                  {artifact.name}
                </p>
                <p style={{ margin: '0 0 4px 0', fontSize: 13, color: theme.text }}>
                  <strong>Path:</strong> {artifact.path}
                </p>
                {artifact.description && (
                  <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary }}>
                    <strong>Description:</strong> {artifact.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
